import { Alert, Button, Field, Modal, TextInput, useToast } from "@nabble/ui";
import { type FormEvent, useState } from "react";
import { confirmPasskeyEnrollment, startPasskeyEnrollment } from "../../../../api/secondFactors";
import { createPasskey } from "../../../../auth/webauthn";
import { passkeyErrorMessage } from "../../../../components/passkeyErrorMessage";

const DEFAULT_PASSKEY_LABEL = "Passkey";

const REGISTER_ERRORS = {
  server: "We couldn't register the passkey.",
  browser: "We couldn't register the passkey. If you canceled, try again.",
};

/** Names a new passkey, then lets the browser create it. */
export function AddPasskeyDialog({ onClose, onAdded }: { onClose: () => void; onAdded: () => void }): React.JSX.Element {
  const { show } = useToast();
  const [label, setLabel] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e?: FormEvent): Promise<void> {
    e?.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const options = await startPasskeyEnrollment();
      const credential = await createPasskey(options as Record<string, unknown>);
      await confirmPasskeyEnrollment(credential, label.trim() || DEFAULT_PASSKEY_LABEL);
      show({ title: "Passkey registered", tone: "success" });
      onAdded();
    } catch (err) {
      setError(passkeyErrorMessage(err, REGISTER_ERRORS));
    } finally {
      setLoading(false);
    }
  }

  return (
    <Modal
      open
      icon="key"
      title="Add passkey"
      description="Give it a name so you can recognize it. Then your browser will ask you to confirm it."
      onClose={onClose}
      actions={
        <>
          <Button label="Cancel" variant="secondary" onClick={onClose} />
          <Button label="Continue" icon="key" loading={loading} onClick={() => void submit()} />
        </>
      }
    >
      <form onSubmit={(e) => void submit(e)} className="nb-stack">
        <Field label="Name" help="For example: Work laptop, iPhone.">
          <TextInput value={label} onChange={(e) => setLabel(e.target.value)} placeholder={DEFAULT_PASSKEY_LABEL} autoComplete="off" />
        </Field>
        {error ? <Alert tone="danger">{error}</Alert> : null}
      </form>
    </Modal>
  );
}
